import { useFlags } from 'launchdarkly-react-client-sdk';
import { FROST_FEATURE_KEYS } from '../../../components/FrostedGate/frosted-gate.constants';
import { useFeatureEntitlement } from '../../../components/FrostedGate/useIsFrosted';
import { PermissionKey } from '../../../services/PermissionAndRolesKeys';
import { PermissionService } from '../../../services/PermissionService';
import { MaggieFeatureFlags } from '../../../util/feature-flags';

/** `showDealTab` picks the customers until `useFeatureEntitlements` hands that to the plan. A
 *  tenant without the plan feature keeps the tab while frosted previews are on, so the preview can show. */
export function isDealsTabEnabled(
  canView: boolean,
  flags: Partial<MaggieFeatureFlags>,
  isEntitled: boolean,
  isChecking = false
): boolean | undefined {
  if (!canView) {
    return false;
  }
  if (!flags.useFeatureEntitlements) {
    return Boolean(flags.showDealTab);
  }
  if (flags.showFrostedModules) {
    return true;
  }
  // The plan alone decides here, so wait for it rather than show a tab that may then vanish.
  return isChecking ? undefined : isEntitled;
}

/** Single gate for the Deals tab, its route and the drawer cards that link into it. `undefined` while the
 *  site's plan is still loading and would decide. */
export function useDealsTabState(): boolean | undefined {
  const flags = useFlags<MaggieFeatureFlags>();
  const { isEntitled, isChecking } = useFeatureEntitlement(FROST_FEATURE_KEYS.deals);
  const canView = PermissionService.get().hasPermission(PermissionKey.canViewMetrics);

  return isDealsTabEnabled(canView, flags, isEntitled, isChecking);
}

/** The tab and the drawer cards stay hidden until the plan has loaded. */
export function useDealsTabEnabled(): boolean {
  return useDealsTabState() === true;
}
